import { Box, CircularProgress, Tab, Tabs } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { colors } from "../../ui/colors";
import { WorkforceForm } from "./WorkforceForm";
import { WorkforceFormCoverage } from "./WorkforceFormCoverage";
import { WorkforceScheduleBlocks } from "./WorkforceScheduleBlocks";
// @ts-ignore
import { getWorkforceByUid } from "../../../utils";

function TabPanel({ value, index, children }: { value: number; index: number; children: React.ReactNode }) {
  return (
    <div style={{ display: value === index ? "block" : "none" }}>
      <Box sx={{ pt: 2 }}>{children}</Box>
    </div>
  );
}

const TABS_SX = {
  mb: 1,
  minHeight: 44,
  borderBottom: `1px solid ${colors.gray[100]}`,
  "& .MuiTabs-indicator": {
    backgroundColor: colors.orange.base,
    height: 2.5,
    borderRadius: "2px 2px 0 0",
  },
  "& .MuiTab-root": {
    textTransform: "none",
    fontWeight: 500,
    fontSize: "0.85rem",
    minHeight: 44,
    color: colors.gray[400],
    "&.Mui-selected": { color: colors.orange.base, fontWeight: 600 },
  },
};

interface WorkforceDetailTabsProps {
  workforce?: any;
  workforceUid?: string;
  onUpdated?: () => void | Promise<void>;
}

export const WorkforceDetailTabs: React.FC<WorkforceDetailTabsProps> = ({ workforce: initialWorkforce, workforceUid: propUid, onUpdated }) => {
  const [tab, setTab] = useState(0);
  const queryClient = useQueryClient();

  const uid = propUid || initialWorkforce?.uid;
  const token = sessionStorage.getItem("token");

  const { data: fetchedWorkforce, isLoading } = useQuery<any>({
    queryKey: ["workforce", uid],
    queryFn: () => getWorkforceByUid(token, uid!),
    enabled: !!uid && (!initialWorkforce || Object.keys(initialWorkforce).length === 0),
    initialData: initialWorkforce,
  });

  const workforce = fetchedWorkforce || initialWorkforce || null;
  const [localWorkforce, setLocalWorkforce] = useState<any>(workforce);

  useEffect(() => {
    if (workforce) {
      setLocalWorkforce(workforce);
    }
  }, [workforce?.uid, workforce?.updatedAt, workforce]);

  const handleRefresh = useCallback(async () => {
    if (localWorkforce?.uid) {
      await queryClient.invalidateQueries({ queryKey: ["workforce", localWorkforce.uid] });
      const cached = queryClient.getQueryData<any>(["workforce", localWorkforce.uid]);
      if (cached) setLocalWorkforce(cached);
    }

    await onUpdated?.();
  }, [localWorkforce?.uid, queryClient, onUpdated]);

  if (isLoading && !localWorkforce) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress size={24} sx={{ color: colors.orange.base }} />
      </Box>
    );
  }

  if (!localWorkforce) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress size={24} sx={{ color: colors.orange.base }} />
      </Box>
    );
  }

  const firstCoverageArea = localWorkforce?.coverageAreas?.[0];
  const initialSearchAddress = firstCoverageArea?.address
    ? firstCoverageArea.address
    : localWorkforce?.address
      ? `${localWorkforce.address}${localWorkforce.city ? ", " + localWorkforce.city : ""}${localWorkforce.state ? ", " + localWorkforce.state : ""}${localWorkforce.zipCode ? " " + localWorkforce.zipCode : ""}`.trim()
      : undefined;

  return (
    <Box sx={{ px: 2, pb: 2 }}>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={TABS_SX}>
        <Tab label="Workforce Info" />
        <Tab label="Coverage Areas" />
        <Tab label="Schedule" />
      </Tabs>

      <TabPanel value={tab} index={0}>
        <WorkforceForm
          key={localWorkforce.updatedAt ?? localWorkforce.uid}
          workforce={localWorkforce}
          workforceUid={localWorkforce.uid}
          onUpdate={handleRefresh}
        />
      </TabPanel>

      <TabPanel value={tab} index={1}>
        <WorkforceFormCoverage
          workforceUid={localWorkforce.uid}
          coverageAreas={localWorkforce.coverageAreas || []}
          address={initialSearchAddress}
          onUpdate={handleRefresh}
        />
      </TabPanel>

      <TabPanel value={tab} index={2}>
        <WorkforceScheduleBlocks workforceUid={localWorkforce.uid} active={tab === 2} />
      </TabPanel>
    </Box>
  );
};
